import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { RuleResult } from '../../types';

/**
 * Renders a single driver-rule result: pass/fail chip, label, expandable
 * violations list. Each rule has a known JSON shape from agnus's
 * eligibility evaluator (see preferredPartner/eligibility/driver.go), and
 * we render a per-rule, carrier-friendly summary instead of raw JSON.
 *
 * If the shape ever drifts (new evaluator version, missing field), we fall
 * back to JSON.stringify so the carrier still sees something rather than a
 * blank row.
 */
@Component({
  selector: 'lohi-rule-result-row',
  templateUrl: './rule-result-row.component.html',
  styleUrls: ['./rule-result-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RuleResultRowComponent {
  @Input() public ruleKey!: string;
  @Input() public result!: RuleResult | null | undefined;

  public expanded = false;

  public get violations(): any[] {
    return this.result?.violations ?? [];
  }

  public toggle(): void {
    if (this.violations.length > 0) {
      this.expanded = !this.expanded;
    }
  }

  public getOrderId(v: any): string | number | null {
    return v?.order_id ?? null;
  }

  public getEventTime(v: any): string | null {
    switch (this.ruleKey) {
      case 'noMissedAcceptances':
      case 'no_missed_acceptances':
        return v?.flicked_at ?? null;
      case 'noLoadRejections':
      case 'no_load_rejections':
        return v?.created_at ?? null;
      case 'noLateArrivals':
      case 'no_late_arrivals':
        return v?.created_at ?? null;
      default:
        return null;
    }
  }

  public getDescription(v: any): string {
    switch (this.ruleKey) {
      case 'gpsAlwaysOn':
      case 'gps_always_on': {
        const count = Number(v?.waypoint_count ?? 0);
        const dur = v?.duration_min != null ? Math.round(Number(v.duration_min)) : null;
        const pingWord = count === 1 ? 'ping' : 'pings';
        if (dur != null) {
          return `Only ${count} GPS ${pingWord} logged during about ${dur} min on the load (we expect at least 1 per minute).`;
        }
        return `Only ${count} GPS ${pingWord} logged on this load (we expect at least 1 per minute).`;
      }
      case 'noMissedAcceptances':
      case 'no_missed_acceptances':
        return 'The driver did not accept the dispatch in time, so the load was reassigned to someone else.';
      case 'noLoadRejections':
      case 'no_load_rejections':
        return 'The driver rejected this load.';
      case 'noLateArrivals':
      case 'no_late_arrivals':
        return 'The driver took more than 2× their predicted ETA AND was more than 2 hours late picking up.';
      default:
        return '';
    }
  }

  public getReason(v: any): string | null {
    if (this.ruleKey === 'noLoadRejections' || this.ruleKey === 'no_load_rejections') {
      const reason = typeof v?.reason === 'string' ? v.reason.trim() : '';
      if (reason) {
        return reason;
      }
    }
    return null;
  }

  public getStatusNote(v: any): string | null {
    if (this.ruleKey === 'noLateArrivals' || this.ruleKey === 'no_late_arrivals') {
      if (v?.resolved_at) {
        return 'The driver did eventually arrive at pickup.';
      }
    }
    return null;
  }

  /**
   * If the violation lacks the basic `order_id` field we treat the shape
   * as unknown (e.g. evaluator added a new rule, schema drift) and fall
   * back to JSON so the carrier still has something to look at.
   */
  public hasFriendlyShape(v: any): boolean {
    return v != null && typeof v === 'object' && v.order_id != null;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public renderViolation(violation: unknown): string {
    if (violation == null) {
      return '';
    }
    if (typeof violation === 'string' || typeof violation === 'number' || typeof violation === 'boolean') {
      return String(violation);
    }
    try {
      return JSON.stringify(violation, null, 2);
    } catch {
      return String(violation);
    }
  }
}
